<ng-template
	#period
	let-periodType="periodType"
	let-range="range"
	let-reduceDayPeriod="reduceDayPeriod"
	let-title="title"
>
	<div class="period">
		<h4 class="label">{{ title | translate }}</h4>
		<div class="range">
			<alaris-dashboard-time-filter
				[formControl]="range"
				[periodTypeControl]="periodType"
				[toLocalDate]="true"
				data-testid="periodDateRange"
				size="sm"
			>
			</alaris-dashboard-time-filter>
		</div>
	</div>
</ng-template>

<ng-template
	#barChart
	let-collapsed="collapsed"
	let-hint="hint"
	let-labelsX="labelsX"
	let-labelsY="labelsY"
	let-legend="legend"
	let-loading$="loading$"
	let-maxChartHeight="maxChartHeight"
	let-stubChart="stubChart"
	let-value="value"
>
	<div [alarisLoading]="loading$" [backdrop]="false" class="bar-chart">
		@if (value.length) {
			<tui-axes [horizontalLines]="5" [axisXLabels]="labelsX" axisY="none" [axisYLabels]="labelsY" class="chart">
				<tui-bar-chart
					[collapsed]="collapsed ?? true"
					[max]="maxChartHeight"
					[size]="null"
					[tuiHintContent]="hint"
					[value]="value"
				>
				</tui-bar-chart>
			</tui-axes>
		} @else {
			<ng-container *ngTemplateOutlet="stubChart"></ng-container>
		}

		<ng-container
			*ngTemplateOutlet="
				barChartLegend;
				context: {
					legend,
				}
			"
		>
		</ng-container>
	</div>
</ng-template>

<ng-template #barChartLegend let-legend="legend">
	<div class="legend">
		@for (label of legend; let index = $index; track index) {
			<tui-legend-item [color]="cu.chartHintColorMapper(label)" [text]="label | translate" size="s"> </tui-legend-item>
		}
	</div>
</ng-template>

<ng-template #totalsProgressCharts let-totalMessages="totalMessages" let-totals="totals">
	<div class="circle-charts">
		@for (total of totals; track $index) {
			<div class="circle-chart">
				<alaris-progress-circle [color]="total.color" [percentageComplete]="100 * (total.total / totalMessages)">
				</alaris-progress-circle>
				<div class="label">
					<div>{{ total.label | translate }}</div>
					<div class="total">{{ total.total }}</div>
				</div>
			</div>
		}
	</div>
</ng-template>

<ng-template
	#statistic
	let-costDelta="costDelta"
	let-sentDelta="sentDelta"
	let-totalCost="totalCost"
	let-totalMessages="totalMessages"
>
	<div class="statistic">
		<div class="label">
			{{ 'dashboard.totalSent' | translate }}
			<div class="total">{{ totalMessages }}</div>
			@if (sentDelta !== undefined) {
				<ng-container *ngTemplateOutlet="delta; context: { dataDelta: sentDelta }"></ng-container>
			}
		</div>
		<div class="label">
			{{ 'campaigns.totalCost' | translate }}
			<div class="total">{{ totalCost | currency: bs.currencyCode() }}</div>
			@if (costDelta !== undefined) {
				<ng-container *ngTemplateOutlet="delta; context: { dataDelta: costDelta }"></ng-container>
			}
		</div>
	</div>
</ng-template>

<ng-template
	#totalStatistics
	let-costDelta="costDelta"
	let-sentDelta="sentDelta"
	let-totalCost="totalCost"
	let-totalMessages="totalMessages"
	let-totals="totals"
>
	<div class="total-statistic">
		<ng-container
			*ngTemplateOutlet="
				totalsProgressCharts;
				context: {
					totalMessages,
					totals,
				}
			"
		>
		</ng-container>
		<ng-container
			*ngTemplateOutlet="
				statistic;
				context: {
					totalCost,
					totalMessages,
				}
			"
		>
		</ng-container>
	</div>
</ng-template>

<ng-template #delta let-dataDelta="dataDelta">
	<div class="delta">
		@if (dataDelta > 0) {
			<alaris-icon class="icon" name="icon-arrow-up"></alaris-icon>
		}
		@if (dataDelta < 0) {
			<alaris-icon class="icon" name="icon-arrow-down"></alaris-icon>
		}
		<span [style.color]="dataDelta < 0 ? '#e31f14' : '#058e02'">{{ dataDelta }}%</span>
		{{ 'dashboard.vsLastPeriod' | translate }}
	</div>
</ng-template>

<ng-template #barChartSkeleton>
	<tui-axes [horizontalLines]="5" axisY="none" class="chart stub-chart">
		<tui-bar-chart [collapsed]="false" [max]="10000" [size]="null" [value]="randomBarArray"> </tui-bar-chart>
	</tui-axes>
</ng-template>

<ng-template #chartVisibility let-configs="configs" let-themeType="themeType">
	<div class="box">
		@for (config of configs; track config) {
			<alaris-checkbox
				[formControl]="config.control"
				[class]="config.type"
				[class.secondary]="!config.control.value"
				[class.dark]="themeType === 'dark'"
				data-testid="chartVisibility"
			>
				{{ 'enums.' + config.type | translate }}
				<span class="bold">{{ config.percent + '%' }}</span>
			</alaris-checkbox>
		}
	</div>
</ng-template>

<ng-template
	#lineCharts
	let-configs="configs"
	let-hint="hint"
	let-horizontalLines="horizontalLines"
	let-labelsX="labelsX"
	let-labelsY="labelsY"
	let-loading$="loading$"
	let-maxChartHeight="maxChartHeight"
	let-stubChart="stubChart"
	let-themeType="themeType"
	let-value="value"
>
	@if (value.length) {
		<tui-axes
			[alarisLoading]="loading$"
			[axisXLabels]="labelsX"
			[axisYLabels]="labelsY"
			[horizontalLines]="horizontalLines ?? 4"
			[tuiLineChartHint]="hint"
			axisY="none"
			class="line-chart"
		>
			@for (config of configs; track config; let index = $index) {
				<tui-line-chart
					[smoothingFactor]="0"
					class="line"
					[class.dark]="themeType === 'dark'"
					[class.transparent]="!config.control.value"
					[height]="maxChartHeight"
					[value]="value[index]"
					[width]="value[index].length - 1"
					[x]="0"
					[y]="0"
				>
				</tui-line-chart>
			}
		</tui-axes>
	} @else {
		<ng-container *ngTemplateOutlet="stubChart"></ng-container>
	}
</ng-template>

<ng-template #lineChartSkeleton let-horizontalLines="horizontalLines">
	<tui-axes [horizontalLines]="horizontalLines ?? 4" axisY="none" class="stub-line-chart">
		@for (chart of [1, 2]; track chart; let index = $index) {
			<tui-line-chart [height]="30" [smoothingFactor]="0" [value]="[]" [width]="30" [x]="0" [y]="0" class="line">
			</tui-line-chart>
		}
	</tui-axes>
</ng-template>
